import React, { useState, useEffect } from 'react';
import { Heart, Gift } from 'lucide-react';
import { Button } from '../components/ui';
import { toast } from '../components/ui/Toaster';
import PaymentService from '../services/paymentService';

interface DonationOption {
  amount: number;
  label: string;
  description: string;
  popular?: boolean;
}

const DonatePage: React.FC = () => {
  const [selectedAmount, setSelectedAmount] = useState<number>(10);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [isCustom, setIsCustom] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [eventName, setEventName] = useState<string>('');

  // Get URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const event = urlParams.get('event');
    if (event) {
      setEventName(decodeURIComponent(event));
    }
  }, []);

  const donationOptions: DonationOption[] = [
    {
      amount: 5,
      label: '€5',
      description: 'Een groot biertje, proost!'
    },
    {
      amount: 10,
      label: '€10',
      description: 'Ik kan niet wachten tot het volgende feestje!',
      popular: true
    },
    {
      amount: 25,
      label: '€25',
      description: 'Ik doe een dansje op jullie!'
    }
  ];

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setIsCustom(false);
    setCustomAmount('');
  };

  const handleCustomAmount = (value: string) => {
    setCustomAmount(value);
    setIsCustom(true);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setSelectedAmount(numValue);
    }
  };

  const handleDonate = async () => {
    const amount = isCustom ? parseFloat(customAmount) : selectedAmount;
    
    if (!amount || amount < 1) {
      toast.error('Fout', 'Voer een geldig bedrag in (minimaal €1)');
      return;
    }

    if (amount > 1000) {
      toast.error('Fout', 'Maximaal donatiebedrag is €1.000,00');
      return;
    }

    setIsProcessing(true);

    try {
      console.log('Creating iDEAL donation payment:', {
        amount,
        eventName: eventName || undefined
      });

      // Create real Mollie iDEAL payment
      const paymentResponse = await PaymentService.createDonationPayment({
        amount: amount,
        eventName: eventName || undefined
      });

      console.log('Payment created successfully:', paymentResponse);

      // Handle response - apiPost extracts the data part already
      if (paymentResponse && paymentResponse.checkoutUrl) {
        // Redirect to Mollie iDEAL checkout
        toast.success('Betaling aangemaakt!', 'Je wordt doorgestuurd naar iDEAL...');
        
        // Small delay to show the success message
        setTimeout(() => {
          PaymentService.redirectToCheckout(paymentResponse.checkoutUrl);
        }, 1000);
      } else {
        throw new Error('Geen checkout URL ontvangen van betaalservice');
      }

    } catch (error: any) {
      console.error('💥 Donation payment error:', error);
      const errorMessage = error.message || 'Er ging iets mis bij het aanmaken van je betaling';
      toast.error('Fout', errorMessage);
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center gap-2.5">
          <span className="sf-logo-mark h-[34px] w-[34px] text-[19px]">S</span>
          <span className="text-xl font-extrabold tracking-tight text-fg-bright">SOLOFIX</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-8 pb-16">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <div className="sf-eyebrow mb-3">// doneer</div>
          <Heart className="w-14 h-14 mx-auto mb-5 text-neon-pink" />
          <h2 className="sf-h1 mb-4">
            Steun <span className="sf-gradient-text">SoloFix</span> 💝
          </h2>
          {eventName && (
            <p className="text-lg text-fg-body mb-3">
              Gefeliciteerd met je groep voor <strong className="text-fg-bright">{eventName}</strong>! 🎉
            </p>
          )}
          <p className="text-lg text-fg-muted leading-relaxed">
            Help ons om SoloFix gratis te houden en anderen te helpen hun perfecte maatjes te vinden
          </p>
        </div>

        <div className="sf-card p-6 sm:p-8">
          {/* Donation Amount Selection */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-fg-bright mb-6">
              Kies een bedrag
            </h3>

            {/* Preset Amounts - Vertical Stack */}
            <div className="space-y-4 mb-6">
              {donationOptions.map((option) => (
                <button
                  key={option.amount}
                  onClick={() => handleAmountSelect(option.amount)}
                  className={`relative w-full p-5 rounded-item border transition-all text-left ${
                    selectedAmount === option.amount && !isCustom
                      ? 'border-neon-pink/40 bg-neon-card'
                      : 'border-white/[.08] bg-white/[.03] hover:border-white/[.18]'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-fg-bright">
                          {option.label}
                        </span>
                        {option.popular && (
                          <span className="sf-badge-lime">Populair</span>
                        )}
                      </div>
                      <div className="text-sm text-fg-muted">
                        {option.description}
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 ${
                      selectedAmount === option.amount && !isCustom
                        ? 'border-neon-pink bg-neon-pink'
                        : 'border-white/[.25]'
                    }`}>
                      {selectedAmount === option.amount && !isCustom && (
                        <div className="w-2 h-2 bg-ink rounded-full m-1"></div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="sf-item border border-white/[.08] p-4">
              <label className="block text-sm font-semibold text-fg-soft mb-2">
                Of voer een ander bedrag in:
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-faint">
                  €
                </span>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={customAmount}
                  onChange={(e) => handleCustomAmount(e.target.value)}
                  placeholder="0.00"
                  className="sf-input pl-8"
                />
              </div>
            </div>
          </div>

          {/* Donate Button */}
          <div className="mb-6">
            <Button
              onClick={handleDonate}
              loading={isProcessing}
              disabled={!selectedAmount || selectedAmount < 1}
              className="w-full py-4 text-lg animate-neon-pulse"
            >
              <Gift className="w-5 h-5 mr-2" />
              Doneer €{(selectedAmount || 0).toFixed(2)}
            </Button>
          </div>

          {/* Footer Note */}
          <div className="text-center text-sm text-fg-muted">
            <p className="mb-2">
              💳 Veilig betalen via iDEAL
            </p>
            <p>
              Je donatie helpt ons om SoloFix te verbeteren en gratis te houden voor iedereen. Dankjewel! ❤️
            </p>
          </div>
        </div>

        {/* Back to SoloFix */}
        <div className="text-center mt-8">
          <a
            href="/"
            className="sf-link inline-flex items-center"
          >
            ← Terug naar SoloFix
          </a>
        </div>
      </div>
    </div>
  );
};

export default DonatePage;